import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { importElfGameJS } from '../../../controllers/elf/items/import'
import { useContent } from '../../../redux/reducer/content'
import { useElfStateControls } from '../../../hooks/useElfStateControls'
import { useUser } from '../../../redux/reducer/user'
import { useElf } from '../../../redux/reducer/elf'
import { getTypeBySlotType, lowerFirstLetter, randomChoice } from '../../../utils/helpers/helpers'
import { useClothType } from '../../../hooks/useClothType'
import { soundManager } from '../../../utils/scene/sound/SoundManager'
import { SOUND_DELAY } from '../../../constants/components/scenes/elf/settings'
import classNames from 'classnames'
import { getSlots } from '../../../utils/getSlots'

import { allTypes, SLOT_TO_3D_TYPE } from '../../../constants/elf3d'
import { useModal } from '../../../hooks/useModal'
import { conturs, defaultContur } from '../../../translations'
import { buildGiftMeModal } from '../../../utils/buildGiftMeModal'
import { GIFT_ME_INTERACTIVE_NAME } from '../../../hooks/useGiftMeInteractive'
import useStateControls from '../../../hooks/useStateControls'

const Elf3D = forwardRef(function Elf3D(
  { callbacks: appCallbacks, clothItems, enableClickAnimations = true, className },
  ref,
) {
  const { elfActions, isVisible } = useElf()
  const { state, slots, notFilteredShopItems } = useContent()
  const { profile } = useUser()
  const [wrapper, setWrapper] = useState()
  const elfStates = useElfStateControls(wrapper)
  const getClothTypeById = useClothType()
  const elfWrapperRef = useRef()
  const canvasRef = useRef()
  const { addModal, closeModal } = useModal()
  const { setState } = useStateControls()
  useImperativeHandle(ref, () => ({ el: elfWrapperRef.current }))

  const characterSlots = useMemo(() => {
    return (slots ?? []).reduce((acc, { type, system_name }) => {
      if (getTypeBySlotType(type) === 'character') acc.push(system_name)
      return acc
    }, [])
  }, [slots])

  const setVisibleCallback = () => !isVisible && appCallbacks.setIsVisible(true)

  const setIsDressedCallback = (isDressed) => appCallbacks.setIsDressed(isDressed)

  const getItem3DTypes = (item = {}) =>
    getSlots(item).reduce((acc, slot) => {
      const type = SLOT_TO_3D_TYPE[slot]
      return type ? [...new Set([...acc, type])] : acc
    }, [])

  useEffect(() => {
    if (!elfStates) return

    const middlewares = (name) => {
      elfStates.setIdle()

      const middlewareLogic = {
        beforeBeauty: () => {
          appCallbacks.setIsAnimation(false)
        },
      }

      return middlewareLogic[name]
    }

    const finish = ({ data: { name, activeState } }) => {
      if (name === 'idle-01') return
      appCallbacks.resetAnimation({ animation: activeState })
      middlewares(activeState)?.()
    }

    const elfClick = () => {
      const isHaveActive = wrapper
        .getAnimationsStats()
        .some(({ name, running }) => name !== 'idle-01' && running)

      if (!isHaveActive && enableClickAnimations)
        appCallbacks.addAnimations([
          {
            state: 'any',
            animation: randomChoice('interaction', 'happyInteraction'),
          },
        ])
    }

    const giftMeClick = () => {
      if (defaultContur !== conturs.me) {
        return
      }

      addModal(buildGiftMeModal({ closeModal, setState }))
    }

    const { eventBus } = wrapper

    const eventsData = [
      { type: 'animation-model:finish', func: finish },
      { type: `${GIFT_ME_INTERACTIVE_NAME}:click`, func: giftMeClick },
      { type: 'elf:click', func: elfClick },
    ]

    const eventsCallback = (action) => {
      eventsData.forEach(({ type, func }) => {
        eventBus[`${action}EventListener`](type, func)
      })
    }

    eventsCallback('add')
    return () => eventsCallback('remove')
  }, [elfStates, addModal, closeModal, enableClickAnimations, setState])

  useEffect(() => {
    if (!wrapper || !elfActions?.length || !isVisible) return

    const { state: animationState, animation } = elfActions[0]

    let soundTimeout = null

    if (animationState === 'any' || state === animationState) {
      elfStates.setState(animation)
      if (!animation.startsWith('beauty')) return
      const soundName = lowerFirstLetter(animation.split('beauty')[1])
      soundTimeout = setTimeout(() => soundManager.onPlay(soundName), SOUND_DELAY[soundName])
    }

    return () => soundTimeout && clearTimeout(soundTimeout)
  }, [elfActions, wrapper, state, isVisible])

  useEffect(() => {
    if (!wrapper) return

    // Ждём, пока подтянутся shopItems, чтобы сразу использовать CDN image_gltf
    // и не делать первую загрузку из локального /assets.
    if (!Array.isArray(notFilteredShopItems) || !notFilteredShopItems.length) return

    setIsDressedCallback(false)

    //! TODO: удалить в будущем
    // Обогащаем модель из shopItems:
    // подмешиваем image_glb/image_gltf с CDN, если они есть в notFilteredShopItems.
    // Матчим и по id, и по system_name на случай расхождений.
    const enrichClothesWithShopData = (clothesItems = []) =>
      clothesItems.map((item) => {
        const shopItem =
          notFilteredShopItems?.find(({ id }) => id === item.id) ??
          notFilteredShopItems?.find(({ system_name }) => system_name === item.system_name)

        return {
          ...item,
          slot: shopItem?.slot ?? item.slot,
          image_glb: shopItem?.image_glb ?? item.image_glb,
          image_gltf: shopItem?.image_gltf ?? item.image_gltf,
        }
      })

    const clothes = enrichClothesWithShopData(clothItems ?? profile?.items)

    if (!clothes) {
      wrapper.setDefaultClothes()
      setVisibleCallback()
      return
    }

    // Надеваем все переданные предметы:
    // - image: текстура одежды
    // - image_gltf: CDN‑ссылка на GLTF, если есть
    const putCloth = async (clothes = []) => {
      await Promise.all(
        clothes.map((item) =>
          wrapper.putClothOn({
            id: item.id,
            slot: item.slot,
            system_name: item.system_name,
            image: item.image,
            image_glb: item.image_glb,
            image_gltf: item.image_gltf,
          }),
        ),
      )
      setIsDressedCallback(true)
      setVisibleCallback()
    }

    const selClothes = clothes.filter(
      ({ is_equipped, id }) => is_equipped && getClothTypeById(id) === 'character',
    )

    const hideObjects = allTypes.filter(
      (type) => !selClothes.some((item) => getItem3DTypes(item).includes(type)),
    )

    if (hideObjects?.length) wrapper.hideSuperfluous(hideObjects)

    const allEquippedSlots = selClothes?.reduce(
      (acc, item) => [...new Set([...acc, ...getSlots(item)])],
      [],
    )

    const missingClothes = characterSlots.filter((slot) => !allEquippedSlots.includes(slot))

    if (missingClothes.length) {
      if (!clothItems) {
        const missingClothData = missingClothes.reduce((acc, slot) => {
          const defaultCloth = notFilteredShopItems?.find(
            ({ slot: sl, is_default }) => sl === slot && is_default,
          )
          const { id } = defaultCloth ?? {}
          if (typeof id === 'number') {
            appCallbacks.equipItem(id)
            acc.push({ id, slots: getSlots(defaultCloth) })
          }
          return acc
        }, [])

        const missingIds = [...new Set(missingClothData?.map(({ id }) => id))]
        const missingSlots = [
          ...new Set(missingClothData?.reduce((acc, { slots }) => [...acc, ...slots], [])),
        ]

        const newProfileItems = profile?.items.map((clothItem) => {
          const { id, is_equipped } = clothItem
          const itemSlots = getSlots(clothItem)
          const isMissing = missingIds.includes(id)
          const isEquipped =
            isMissing || (!missingSlots.some((slot) => itemSlots.includes(slot)) && is_equipped)
          return { ...clothItem, is_equipped: isEquipped }
        })

        // changeProfile пересоздаёт profile.items и перезапускает этот эффект,
        // поэтому диспатчим только при реальном изменении экипировки — иначе цикл рендеров
        const isEquipmentChanged = newProfileItems.some(
          ({ is_equipped }, index) => is_equipped !== profile?.items?.[index]?.is_equipped,
        )

        if (isEquipmentChanged) {
          appCallbacks.changeProfile({ items: newProfileItems })
        }
      } else {
        wrapper.setDefaultClothes()
        const defaultClothes = ['pants', 'outerwear', 'hair'].map((sysName) => {
          const { id, slot, system_name, image, image_glb, image_gltf } =
            notFilteredShopItems?.find(
              ({ system_name, is_default }) => system_name.startsWith(sysName) && is_default,
            )
          return { id, slot, system_name, image, image_glb, image_gltf }
        })
        putCloth(defaultClothes)
        return
      }
    }

    putCloth(selClothes)
  }, [profile?.items, characterSlots, wrapper, clothItems, notFilteredShopItems, getClothTypeById])

  useEffect(() => {
    let wrapper
    ;(async () => {
      const data = await importElfGameJS()
      wrapper = data.wrapper
      wrapper.controller.onAppear(canvasRef.current)
      setWrapper(wrapper)
    })()

    return () => {
      wrapper?.controller?.onUnmount()
    }
  }, [])

  return (
    <div
      ref={elfWrapperRef}
      className={classNames('elf-3d', className)}
    >
      <div
        ref={canvasRef}
        className={classNames(`elf-3d__content`, {
          'elf-3d__content_visible': isVisible,
        })}
      />
    </div>
  )
})
export default Elf3D
Elf3D.propTypes = {}
